/**
 * @file src/utils/monitorLabel.js
 * The single owner of the Engine Monitor's axis row label.
 *
 * The rows used to read `[xA] Oscillate Sine`. The `A` was the module slot letter; with
 * `MAX_MODULES = 1` there has only ever been one slot per axis, so it named nothing on screen.
 * The dimension is carried by the selected roman numeral in the dimension selector, and only one
 * dimension's rows are ever visible at a time, so the row only has to say which dial it belongs
 * to: `X · Oscillate Sine`. Uppercase matches the X / Y / Z dials driving the same control.
 *
 * Both the React monitor (`InfoPanel`) and the legacy DOM monitor (`EffectsRack`,
 * `clearMonitorDisplay`) build their labels here so the two paths cannot drift.
 *
 * If `MAX_MODULES` ever rises above 1, two rows on the same axis would read `X` — the slot letter
 * would have to come back as a disambiguator, and this is the one place to bring it back.
 */

/** Separator between the dial letter and the audio module name. */
export const MONITOR_AXIS_SEPARATOR = ' · ';

/**
 * Compose one monitor row's label: the dial letter, plus the audio module it drives.
 *
 * @param {string} axis axis key ('x' | 'y' | 'z'); rendered uppercase to match the dials.
 * @param {string|null} [moduleLabel] the audio module's display name, or null/empty when the
 *   slot carries no module (the row then shows the dial letter alone).
 * @returns {string} e.g. `X · Oscillate Sine`, or `X` when there is no module.
 */
export function formatMonitorAxisLabel(axis, moduleLabel) {
    const dial = String(axis ?? '').trim().toUpperCase();
    const name = typeof moduleLabel === 'string' ? moduleLabel.trim() : '';
    // Neither half is padded onto a missing other half: no axis must not open the row with a
    // dangling separator, and no module leaves the dial standing alone.
    if (!dial) return name;
    return name ? `${dial}${MONITOR_AXIS_SEPARATOR}${name}` : dial;
}
